import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Pause, XCircle, AlertCircle, Target } from 'lucide-react';
const statusConfig = {
    active: {
        color: '#0ea5e9',
        bg: 'rgba(14, 165, 233, 0.15)',
        border: 'rgba(14, 165, 233, 0.3)',
        text: '#0ea5e9',
        icon: Target,
        label: 'Active'
    },
    planning: {
        color: '#C9B8A8',
        bg: 'rgba(201, 184, 168, 0.15)',
        border: 'rgba(201, 184, 168, 0.3)',
        text: '#C9B8A8',
        icon: Clock,
        label: 'Planning'
    },
    'on-hold': {
        color: '#9B8B7E',
        bg: 'rgba(155, 139, 126, 0.15)',
        border: 'rgba(155, 139, 126, 0.3)',
        text: '#9B8B7E',
        icon: Pause,
        label: 'On Hold'
    },
    completed: {
        color: '#6B8E6F',
        bg: 'rgba(107, 142, 111, 0.15)',
        border: 'rgba(107, 142, 111, 0.3)',
        text: '#6B8E6F',
        icon: CheckCircle2,
        label: 'Completed'
    },
    cancelled: {
        color: '#C77A58',
        bg: 'rgba(199, 122, 88, 0.15)',
        border: 'rgba(199, 122, 88, 0.3)',
        text: '#C77A58',
        icon: XCircle,
        label: 'Cancelled'
    },
    urgent: {
        color: '#C77A58',
        bg: 'rgba(199, 122, 88, 0.15)',
        border: 'rgba(199, 122, 88, 0.3)',
        text: '#C77A58',
        icon: AlertCircle,
        label: 'Urgent'
    },
    custom: {
        color: '#0ea5e9',
        bg: 'rgba(14, 165, 233, 0.15)',
        border: 'rgba(14, 165, 233, 0.3)',
        text: '#0ea5e9',
        icon: Target,
        label: 'Custom'
    }
};
const sizeClasses = {
    sm: {
        badge: 'h-6 px-2 text-xs gap-1',
        icon: 'w-3 h-3'
    },
    md: {
        badge: 'h-7 px-3 text-sm gap-1.5',
        icon: 'w-3.5 h-3.5'
    },
    lg: {
        badge: 'h-8 px-4 text-base gap-2',
        icon: 'w-4 h-4'
    }
};
export function StatusBadge({ status, label, size = 'md', showIcon = true, animated = true, customColor, className = '' }) {
    const config = statusConfig[status];
    const Icon = config.icon;
    const displayLabel = label || config.label;
    const sizeConfig = sizeClasses[size];
    const badgeStyle = customColor
        ? {
            backgroundColor: `${customColor}26`,
            borderColor: `${customColor}4D`,
            color: customColor
        }
        : {
            backgroundColor: config.bg,
            borderColor: config.border,
            color: config.text
        };
    const BadgeComponent = animated ? motion.div : 'div';
    const animationProps = animated
        ? {
            initial: { opacity: 0, scale: 0.8 },
            animate: { opacity: 1, scale: 1 },
            transition: { duration: 0.2 }
        }
        : {};
    return (_jsxs(BadgeComponent, { className: `
        inline-flex items-center justify-center
        ${sizeConfig.badge}
        rounded-full
        border
        font-medium
        whitespace-nowrap
        ${className}
      `, style: badgeStyle, ...animationProps, children: [showIcon && _jsx(Icon, { className: sizeConfig.icon }), _jsx("span", { children: displayLabel })] }));
}
const priorityConfig = {
    low: {
        color: '#6B8E6F',
        label: 'Low'
    },
    medium: {
        color: '#C9B8A8',
        label: 'Medium'
    },
    high: {
        color: '#0ea5e9',
        label: 'High'
    },
    urgent: {
        color: '#C77A58',
        label: 'Urgent'
    }
};
export function PriorityBadge({ priority, size = 'md', showIcon = true, className = '' }) {
    const config = priorityConfig[priority];
    return (_jsx(StatusBadge, { status: "custom", label: config.label, size: size, showIcon: showIcon, customColor: config.color, className: className }));
}
const dotSizes = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3'
};
export function StatusDot({ status, size = 'md', pulse = false, customColor, className = '' }) {
    const config = statusConfig[status];
    const dotColor = customColor || config.color;
    return (_jsxs("div", { className: `relative inline-flex ${className}`, children: [_jsx("div", { className: `${dotSizes[size]} rounded-full`, style: { backgroundColor: dotColor } }), pulse && (_jsx(motion.div, { className: `absolute inset-0 ${dotSizes[size]} rounded-full`, style: { backgroundColor: dotColor }, animate: {
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5]
                }, transition: {
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                } }))] }));
}
